import { useContext, useEffect, useMemo, useState } from "react";
import { SendButton } from "../MessagePanel.style";
import {
  ApiErrorHandler,
  IChatInfoAccess,
  MessagePanelContext,
  RecipientInfoContext,
  useTranslation,
} from "hhn-commons";
import { IUserId } from "@home-health-notify/domain";
import CommonDialog from "../../../common/CommonDialog/CommonDialog";
import AskForAcknowledgement from "./AskForAcknowledgement";
import MessageInfo from "./MessageInfo";

const RecipientInfo = ({
  chatInfoAccess,
}: {
  chatInfoAccess: IChatInfoAccess;
}) => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "chat" });
  const [isLoading, setIsLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<IUserId[]>([]);
  const {
    menuMessage,
    isRecipientInfoModalOpen,
    setIsRecipientInfoModalOpen,
    recipientInfoType,
  } = useContext(RecipientInfoContext);

  const { clearSelectedMessages } = useContext(MessagePanelContext);

  const closeModal = () => {
    setSelectedUsers([]);
    setIsRecipientInfoModalOpen(false);
  };

  useEffect(() => {
    async function fetchRecipientInfo() {
      try {
        setIsLoading(true);
        if (menuMessage) {
          await menuMessage.fetchRecipientInfo();
        }
      } catch (e) {
        ApiErrorHandler(e);
      } finally {
        setIsLoading(false);
      }
    }

    menuMessage && fetchRecipientInfo();
  }, [menuMessage]);

  const askAcknowledgment = () => {
    try {
      if (menuMessage) {
        menuMessage.askForAcknowledgement({
          usersToAcknowledge: selectedUsers,
        });
      }
    } finally {
      clearSelectedMessages();
      closeModal();
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const recipients = menuMessage?.getMessage().recipientInfo?.visibleTo || [];
  const title =
    recipientInfoType === "MESSAGE_INFO"
      ? tNs("messageInfo.messageInfoHeader")
      : tNs("askForAcknowledgement.askForAcknowledgementHeader");

  const recipientsWithAckReq = useMemo(
    () =>
      recipients.filter(
        recipient => recipient.acknowledgementRequired === 0, // Filtering recipients which require acknowledgment
      ),
    [recipients],
  );
  return (
    <>
      {isRecipientInfoModalOpen && menuMessage && (
        <CommonDialog
          isDialogOpen={isRecipientInfoModalOpen}
          onDialogClose={closeModal}
          title={title}
          titleAlignment="center"
          actionContent={
            recipientInfoType === "ASK_ACKNOWLEGEMENT" && (
              <SendButton
                variant="contained"
                disabled={selectedUsers.length === 0}
                onClick={askAcknowledgment}
              >
                {tNs("askForAcknowledgement.sendBtn")}
              </SendButton>
            )
          }
        >
          {recipientInfoType === "MESSAGE_INFO" ? (
            <MessageInfo
              message={menuMessage}
              recipients={recipients}
              isLoading={isLoading}
              chatInfoAccess={chatInfoAccess}
            />
          ) : (
            <AskForAcknowledgement
              message={menuMessage}
              recipients={recipientsWithAckReq}
              isLoading={isLoading}
              setSelectedUsers={setSelectedUsers}
              selectedUsers={selectedUsers}
              chatInfoAccess={chatInfoAccess}
            />
          )}
        </CommonDialog>
      )}
    </>
  );
};

export default RecipientInfo;
